import { CourseKind, StudentScheduleItemType } from '@duke-hq/svc-api-db/client';

type ScheduleItemForCompletion = {
  semester: number;
  item_type: StudentScheduleItemType;
  unit_of_competency_id: string;
  is_complete: boolean;
};

/**
 * The distinct units that count as completed on a generated enrolment document.
 *
 * - LLN (semester 0) items never count.
 * - Non-vocational courses: a unit counts when every one of its assessment and
 *   conversation items is complete.
 * - Other courses: a completed ASSESSMENT item marks its unit complete, unless
 *   the unit has a WORK_PLACEMENT_WORKBOOK item that is still incomplete.
 */
export function completedUnitOfCompetencyIds(
  scheduleItems: ScheduleItemForCompletion[],
  courseKind: CourseKind,
): string[] {
  const unitsAwaitingWorkPlacement = new Set(
    scheduleItems
      .filter(
        (item) =>
          item.item_type === StudentScheduleItemType.WORK_PLACEMENT_WORKBOOK && !item.is_complete,
      )
      .map((item) => item.unit_of_competency_id),
  );

  const completedScheduleItems = scheduleItems.filter((item) => {
    if (item.semester === 0) {
      return false;
    } else if (courseKind === CourseKind.NON_VOCATIONAL) {
      const nonVocResourcesWithMatchingUnitId = scheduleItems.filter(
        (innerItem) =>
          innerItem.unit_of_competency_id === item.unit_of_competency_id &&
          (innerItem.item_type === StudentScheduleItemType.ASSESSMENT ||
            innerItem.item_type === StudentScheduleItemType.CONVERSATION),
      );
      return (
        (item.item_type === StudentScheduleItemType.ASSESSMENT ||
          item.item_type === StudentScheduleItemType.CONVERSATION) &&
        nonVocResourcesWithMatchingUnitId.length > 0 &&
        nonVocResourcesWithMatchingUnitId.every((i) => i.is_complete)
      );
    } else {
      return (
        item.is_complete &&
        item.item_type === StudentScheduleItemType.ASSESSMENT &&
        !unitsAwaitingWorkPlacement.has(item.unit_of_competency_id)
      );
    }
  });

  return [...new Set(completedScheduleItems.map((item) => item.unit_of_competency_id))];
}
